import type {
  DashboardResponse,
  FloorPlanResponse,
  WorldTileResponse,
} from "@parcelbound/contracts";
import { create } from "zustand";

export type ChunkState = "unseen" | "queued" | "generating" | "ready" | "failed";

export interface Session {
  id: string;
  displayName: string;
  homeChunkId: string | null;
  isStaff?: boolean;
}

/** Windows can be open simultaneously and arranged over the world. */
export type WindowId =
  | "inspector"
  | "portfolio"
  | "exchange"
  | "bank"
  | "leases"
  | "leaderboard"
  | "business"
  | "offers"
  | "community"
  | "admin";

/** Which thematic overlay the map is drawing. */
export type MapLayer = "world" | "foot_traffic" | "logistics";

interface AppState {
  session: Session | null;
  dashboard: DashboardResponse | null;

  openWindows: WindowId[];
  /** Last-focused window, drawn above the others. */
  focused: WindowId | null;

  mapLayer: MapLayer;
  showResources: boolean;
  /** Bumped by the dock avatar; WorldView reacts by recentring on the player. */
  goToPlayerTick: number;

  /** Features per ready chunk, keyed by chunk id. */
  chunks: Record<string, WorldTileResponse>;
  chunkStates: Record<string, ChunkState>;
  selectedId: string | null;
  /** The floor plan the inspector is showing, drawn over the map as a cutaway. */
  floorPlan: FloorPlanResponse | null;
  /** Floor the plan is asked for — shared by the inspector and the world's floor bar. */
  planFloor: number;
  /** Room highlighted in both the plan and the inspector's list. */
  selectedRoomId: string | null;
  toast: { kind: "ok" | "err"; text: string } | null;

  setSession: (s: Session | null) => void;
  setDashboard: (d: DashboardResponse | null) => void;

  openWindow: (id: WindowId) => void;
  closeWindow: (id: WindowId) => void;
  toggleWindow: (id: WindowId) => void;
  focusWindow: (id: WindowId) => void;

  setMapLayer: (l: MapLayer) => void;
  toggleResources: () => void;
  bumpGoToPlayer: () => void;

  setChunk: (chunkId: string, features: WorldTileResponse) => void;
  setChunkStates: (next: Record<string, ChunkState>) => void;
  select: (id: string | null) => void;
  setFloorPlan: (plan: FloorPlanResponse | null) => void;
  setPlanFloor: (floor: number) => void;
  selectRoom: (roomId: string | null) => void;
  notify: (kind: "ok" | "err", text: string) => void;
  clearToast: () => void;
  reset: () => void;
}

export const useApp = create<AppState>((set, get) => ({
  session: null,
  dashboard: null,
  openWindows: [],
  focused: null,
  mapLayer: "world",
  showResources: false,
  goToPlayerTick: 0,
  chunks: {},
  chunkStates: {},
  selectedId: null,
  floorPlan: null,
  planFloor: 0,
  selectedRoomId: null,
  toast: null,

  setSession: (session) => set({ session }),
  setDashboard: (dashboard) => set({ dashboard }),

  openWindow: (id) =>
    set((s) => ({
      openWindows: s.openWindows.includes(id) ? s.openWindows : [...s.openWindows, id],
      focused: id,
    })),
  closeWindow: (id) =>
    set((s) => {
      const openWindows = s.openWindows.filter((w) => w !== id);
      return {
        openWindows,
        focused: s.focused === id ? (openWindows[openWindows.length - 1] ?? null) : s.focused,
        // The docked property card owns the selection now; closing the
        // management window leaves the card (and the cutaway) in place.
      };
    }),
  toggleWindow: (id) => {
    if (get().openWindows.includes(id)) get().closeWindow(id);
    else get().openWindow(id);
  },
  focusWindow: (id) => set({ focused: id }),

  setMapLayer: (mapLayer) => set({ mapLayer }),
  toggleResources: () => set((s) => ({ showResources: !s.showResources })),
  bumpGoToPlayer: () => set((s) => ({ goToPlayerTick: s.goToPlayerTick + 1 })),

  setChunk: (chunkId, features) =>
    set((s) => ({ chunks: { ...s.chunks, [chunkId]: features } })),
  setChunkStates: (next) => set((s) => ({ chunkStates: { ...s.chunkStates, ...next } })),

  // A new plan keeps the selected room when that room is on it — picking a
  // room from the property card switches floors, and the selection has to
  // survive the reload for the buy dialog to open.
  setFloorPlan: (floorPlan) =>
    set((s) => ({
      floorPlan,
      selectedRoomId:
        s.selectedRoomId !== null &&
        floorPlan?.units.some((u) => u.rooms.some((r) => r.id === s.selectedRoomId))
          ? s.selectedRoomId
          : null,
    })),
  setPlanFloor: (planFloor) => set({ planFloor }),
  selectRoom: (selectedRoomId) => set({ selectedRoomId }),

  // Selecting a property surfaces the docked property card (App renders it
  // whenever something is selected); the full inspector window stays a dock
  // tool. A new selection drops the old floor plan rather than leaving
  // another building's rooms on screen while this one's load.
  select: (selectedId) =>
    set((s) => {
      if (selectedId === null) return { selectedId: null, floorPlan: null, planFloor: 0, selectedRoomId: null };
      return {
        selectedId,
        floorPlan: s.selectedId === selectedId ? s.floorPlan : null,
        planFloor: s.selectedId === selectedId ? s.planFloor : 0,
        selectedRoomId: null,
      };
    }),

  notify: (kind, text) => set({ toast: { kind, text } }),
  clearToast: () => set({ toast: null }),
  reset: () =>
    set({
      session: null,
      dashboard: null,
      chunks: {},
      chunkStates: {},
      selectedId: null,
      floorPlan: null,
      planFloor: 0,
      selectedRoomId: null,
      openWindows: [],
      focused: null,
      toast: null,
    }),
}));

/** Aggregate generation progress for the bottom indicator. */
export function generationProgress(states: Record<string, ChunkState>): {
  label: string;
  busy: boolean;
  failed: boolean;
} {
  const values = Object.values(states);
  if (values.length === 0) return { label: "Loading map data…", busy: true, failed: false };

  const failed = values.filter((s) => s === "failed").length;
  const pending = values.filter((s) => s === "unseen" || s === "queued").length;
  const generating = values.filter((s) => s === "generating").length;
  const ready = values.filter((s) => s === "ready").length;

  if (failed > 0 && pending === 0 && generating === 0) {
    return { label: "Generation failed", busy: false, failed: true };
  }
  if (generating > 0) {
    return {
      label: `Generating properties ${ready}/${values.length}…`,
      busy: true,
      failed: false,
    };
  }
  if (pending > 0) {
    return { label: "Waiting for world worker…", busy: true, failed: false };
  }
  return { label: "Map ready", busy: false, failed: false };
}
